import { requestShareTemplates } from './shareTemplateApi';
/**
 * #798 方案 C「分享模板」体系（分享模板库 + 选择弹窗）。
 *
 * 与打印模板（printTemplates.ts，key changfa_print_templates）平行的独立配置体系：
 * - 每种单据类型（送货单 / 报价单 / 采购单）可保存多套分享模板；
 * - 分享时（生成图片 / PDF）弹窗选择用哪套；仅 1 套时直接使用，0 套时回退渲染器内置布局；
 * - 列显示 / 顺序 / 列名 / 字号 / 整单备注 / 合计 / 页脚均可配置。
 *
 * ⚠️ 铁律（延续 #543/#778 打印模板原则）：用户已保存的模板任何升级不删除不重置——
 * 迁移 = 保留用户全部值（含列顺序）+ 补全缺失字段 + 新列 key 追加到末尾。
 *
 * 存储 key `changfa_share_templates` 已加入 backupService SETTINGS_KEYS，
 * 自动获得多设备同步 + 云备份（LS_DOMAINS 由 BUSINESS_DATA_KEYS + SETTINGS_KEYS 派生）。
 */

export type ShareDocType = 'delivery' | 'quotation' | 'purchase';
/** 渲染层 docType（五值，比 ShareDocType 细分：sales/inbound 与 delivery/purchase 共用模板） */
export type ShareRenderDocType = 'quotation' | 'delivery' | 'sales' | 'inbound' | 'po';

export type ShareColumnKey =
  | 'index'
  | 'productName'
  | 'packSize'
  | 'qty'
  | 'subUnitQty'
  | 'subUnit'
  | 'price'
  | 'amount'
  | 'remark';

export interface ShareColumn {
  key: ShareColumnKey;
  label: string;
  visible: boolean;
}

export interface ShareTemplate {
  id: string;
  name: string;
  docType: ShareDocType;
  isDefault: boolean;
  columns: ShareColumn[];
  /** 表格正文字号 px（表头随比例缩小） */
  fontSize: number;
  showOrderRemark: boolean;
  showTotal: boolean;
  showFooter: boolean;
}

export const SHARE_TEMPLATES_STORAGE_KEY = 'changfa_share_templates';
const LAST_PICK_STORAGE_KEY = 'changfa_share_tpl_last_v1';

/** 列元数据：宽度 px（null = 自适应拉伸）/ 对齐 / 默认列名 */
export const SHARE_COLUMN_META: Record<ShareColumnKey, {
  widthPx: number | null;
  align: 'left' | 'center' | 'right';
  defaultLabel: string;
}> = {
  index: { widthPx: 34, align: 'center', defaultLabel: '序号' },
  productName: { widthPx: null, align: 'left', defaultLabel: '产品名称' },
  packSize: { widthPx: 46, align: 'center', defaultLabel: '规格' },
  qty: { widthPx: 52, align: 'center', defaultLabel: '件数' },
  subUnitQty: { widthPx: 64, align: 'center', defaultLabel: '数量' },
  subUnit: { widthPx: 52, align: 'center', defaultLabel: '单位' },
  price: { widthPx: 72, align: 'right', defaultLabel: '单价' },
  amount: { widthPx: 84, align: 'right', defaultLabel: '金额' },
  remark: { widthPx: 90, align: 'left', defaultLabel: '备注' },
};

export const SHARE_DOC_TYPE_LABEL: Record<ShareDocType, string> = {
  delivery: '送货单',
  quotation: '报价单',
  purchase: '采购单',
};

function defaultColumns(docType: ShareDocType): ShareColumn[] {
  const col = (key: ShareColumnKey, label?: string): ShareColumn => ({
    key,
    label: label ?? SHARE_COLUMN_META[key].defaultLabel,
    visible: true,
  });
  // 与渲染器内置 7 列布局保持一致的基础列（无单价 / 无规格）
  const base: ShareColumn[] = [
    col('index'),
    col('productName'),
    col('qty'),
    col('subUnitQty'),
    col('subUnit'),
    col('amount'),
    col('remark'),
  ];
  if (docType === 'quotation') {
    // 报价单默认展示单价：插在金额前
    base.splice(base.findIndex((c) => c.key === 'amount'), 0, col('price'));
  }
  if (docType === 'purchase') {
    // 采购单（发供应商的采购报价单）默认展示进货价：插在金额前
    base.splice(base.findIndex((c) => c.key === 'amount'), 0, col('price', '进货价'));
  }
  return base;
}

function defaultTemplate(docType: ShareDocType, name: string): ShareTemplate {
  return {
    id: `share-${docType}-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`,
    name,
    docType,
    isDefault: false,
    columns: defaultColumns(docType),
    fontSize: 13,
    showOrderRemark: true,
    showTotal: true,
    showFooter: true,
  };
}

/** 三类单据的初始默认模板（首次使用时自动创建，用户可改可删） */
export function createDefaultTemplates(): ShareTemplate[] {
  const delivery = defaultTemplate('delivery', '标准送货单');
  const quotation = defaultTemplate('quotation', '标准报价单');
  const purchase = defaultTemplate('purchase', '标准采购单');
  delivery.isDefault = true;
  quotation.isDefault = true;
  purchase.isDefault = true;
  return [delivery, quotation, purchase];
}

function isShareColumnKey(v: unknown): v is ShareColumnKey {
  return typeof v === 'string' && Object.prototype.hasOwnProperty.call(SHARE_COLUMN_META, v);
}

function toFiniteNumber(v: unknown, fallback: number): number {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function sanitizeLabel(v: unknown, key: ShareColumnKey): string {
  const s = String(v ?? '').trim();
  return s ? s.slice(0, 12) : SHARE_COLUMN_META[key].defaultLabel;
}

/**
 * 保留式迁移（铁律）：不删除不重置——
 * - 保留用户全部值（列顺序、列名、显隐、字号、开关）；
 * - 补全缺失字段（用该 docType 默认值）；
 * - 新列 key（元数据有而用户模板没有的）追加到末尾；
 * - 元数据中已废弃的列 key 原样保留（不丢用户数据）。
 */
function migrateTemplate(raw: unknown, docType: ShareDocType): ShareTemplate | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const id = typeof r.id === 'string' && r.id ? r.id : null;
  if (!id) return null;
  const rawColumns = Array.isArray(r.columns) ? r.columns : [];
  const seen = new Set<string>();
  const columns: ShareColumn[] = [];
  for (const rc of rawColumns) {
    if (!rc || typeof rc !== 'object') continue;
    const key = (rc as Record<string, unknown>).key;
    if (!isShareColumnKey(key) || seen.has(key)) continue;
    seen.add(key);
    columns.push({
      key,
      label: sanitizeLabel((rc as Record<string, unknown>).label, key),
      visible: (rc as Record<string, unknown>).visible !== false,
    });
  }
  // 补全新列到末尾
  const knownKeys = Object.keys(SHARE_COLUMN_META) as ShareColumnKey[];
  for (const key of knownKeys) {
    if (!seen.has(key)) {
      columns.push({
        key,
        label: docType === 'purchase' && key === 'price' ? '进货价' : SHARE_COLUMN_META[key].defaultLabel,
        visible: false,
      });
    }
  }
  const fontSize = Math.max(8, Math.min(18, Math.round(toFiniteNumber(r.fontSize, 13) * 10) / 10));
  return {
    id,
    name: typeof r.name === 'string' && r.name.trim() ? r.name.trim().slice(0, 30) : '未命名模板',
    docType: isShareDocTypeValue(r.docType) ? r.docType : docType,
    isDefault: r.isDefault === true,
    columns,
    fontSize,
    showOrderRemark: r.showOrderRemark !== false,
    showTotal: r.showTotal !== false,
    showFooter: r.showFooter !== false,
  };
}

function isShareDocTypeValue(v: unknown): v is ShareDocType {
  return v === 'delivery' || v === 'quotation' || v === 'purchase';
}

export async function loadShareTemplateStore() {
  const store = await requestShareTemplates();
  const templates = store.templates.map(t => migrateTemplate(t, 'delivery'));
  if (templates.some(t => !t)) throw Error('服务器分享模板格式异常，请核对后再分享');
  return { revision: store.revision, templates: templates as ShareTemplate[] };
}
export async function loadShareTemplates(): Promise<ShareTemplate[]> {
  return (await loadShareTemplateStore()).templates;
}
export async function saveShareTemplates(templates: ShareTemplate[], expectedRevision: number) {
  return requestShareTemplates({templates, expectedRevision});
}
export function createShareTemplate(docType: ShareDocType, name?: string): ShareTemplate {
  return defaultTemplate(docType, name || '新模板');
}

export function duplicateShareTemplate(src: ShareTemplate): ShareTemplate {
  const copy: ShareTemplate = {
    ...src,
    id: `share-${src.docType}-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`,
    name: `${src.name} 副本`.slice(0, 30),
    isDefault: false,
    columns: src.columns.map((c) => ({ ...c })),
  };
  return copy;
}

function readLastPick(): Partial<Record<ShareDocType, string>> {
  try {
    const raw = localStorage.getItem(LAST_PICK_STORAGE_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    return parsed as Partial<Record<ShareDocType, string>>;
  } catch {
    return {};
  }
}

function writeLastPick(docType: ShareDocType, templateId: string): void {
  try {
    const map = readLastPick();
    map[docType] = templateId;
    localStorage.setItem(LAST_PICK_STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* 忽略 */
  }
}

/**
 * 分享前选择模板：
 * - 0 个模板 → 返回 null（调用方回退渲染器内置 7 列布局）；
 * - 1 个模板 → 直接返回（并记住选择）；
 * - 多个 → 弹单选列表（纯 DOM，样式蓝本 shareDocument.ts showShareUrlDialog），
 *   上次选择作为预选项；默认模板排前。
 * 用户取消（点遮罩 / Escape / 关闭）→ 返回 null，调用方中止本次分享。
 */
export async function pickShareTemplate(docType: ShareDocType): Promise<ShareTemplate | null> {
  const all = (await loadShareTemplates()).filter((t) => t.docType === docType);
  if (all.length === 0) return Promise.resolve(null);
  if (all.length === 1) {
    writeLastPick(docType, all[0].id);
    return Promise.resolve(all[0]);
  }

  const sorted = [...all].sort((a, b) => Number(b.isDefault) - Number(a.isDefault));
  const lastId = readLastPick()[docType];
  const preselectId = sorted.some((t) => t.id === lastId) ? lastId : sorted.find((t) => t.isDefault)?.id ?? sorted[0].id;

  return new Promise<ShareTemplate | null>((resolve, reject) => {
    const wrap = document.createElement('div');
    wrap.setAttribute('data-changfa-share-tpl-picker', '1');
    wrap.style.cssText = [
      'position:fixed', 'left:0', 'top:0', 'right:0', 'bottom:0',
      'background:rgba(0,0,0,0.55)', 'z-index:2147483647',
      'display:flex', 'align-items:center', 'justify-content:center',
      'padding:20px', 'box-sizing:border-box',
    ].join(';');

    let selectedId: string | null = null;
    const close = () => {
      if (wrap.parentNode) wrap.parentNode.removeChild(wrap);
      document.removeEventListener('keydown', onKey);
      if (selectedId) resolve(sorted.find((t) => t.id === selectedId) ?? null);
      else reject(Error('已取消分享')); 
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);

    const card = document.createElement('div');
    card.style.cssText = [
      'background:#fff', 'border-radius:16px', 'padding:24px 22px 18px',
      'width:100%', 'max-width:380px', 'box-sizing:border-box',
      'box-shadow:0 12px 40px rgba(0,0,0,0.25)',
      'font-family:-apple-system,"PingFang SC","Microsoft YaHei",sans-serif',
    ].join(';');

    const title = document.createElement('div');
    title.textContent = `选择${SHARE_DOC_TYPE_LABEL[docType]}分享模板`;
    title.style.cssText = 'font-size:16px;font-weight:700;color:#0f766e;margin-bottom:4px';

    const sub = document.createElement('div');
    sub.textContent = '可在「打印设置 → 分享模板」中管理模板';
    sub.style.cssText = 'font-size:12px;color:#6b7280;margin-bottom:14px;line-height:1.5';

    const list = document.createElement('div');
    list.style.cssText = 'display:flex;flex-direction:column;gap:8px;margin-bottom:14px';

    const makeBtn = (text: string, primary: boolean, onClick: () => void) => {
      const b = document.createElement('button');
      b.textContent = text;
      b.style.cssText = [
        'flex:1', 'border:0', 'border-radius:10px', 'padding:10px 0',
        'font-size:14px', 'font-weight:600', 'cursor:pointer',
        primary ? 'background:#0f766e;color:#fff' : 'background:#e8f5f1;color:#0f766e',
      ].join(';');
      b.onclick = onClick;
      return b;
    };

    const rows: HTMLLabelElement[] = [];
    for (const tpl of sorted) {
      const label = document.createElement('label');
      label.style.cssText = [
        'display:flex', 'align-items:center', 'gap:10px',
        'border:1.5px solid #e5e7eb', 'border-radius:10px',
        'padding:10px 12px', 'cursor:pointer', 'user-select:none',
      ].join(';');
      const radio = document.createElement('input');
      radio.type = 'radio';
      radio.name = 'changfa-share-tpl';
      radio.value = tpl.id;
      radio.checked = tpl.id === preselectId;
      radio.style.cssText = 'accent-color:#0f766e;width:16px;height:16px;flex:none';
      const nameSpan = document.createElement('span');
      nameSpan.textContent = tpl.name;
      nameSpan.style.cssText = 'flex:1;font-size:14px;color:#1f2937;font-weight:600';
      const meta = document.createElement('span');
      const visibleCount = tpl.columns.filter((c) => c.visible).length;
      meta.textContent = `${visibleCount} 列 · ${tpl.fontSize}px`;
      meta.style.cssText = 'font-size:11px;color:#9ca3af;flex:none';
      label.appendChild(radio);
      label.appendChild(nameSpan);
      label.appendChild(meta);
      label.onclick = (e) => {
        e.preventDefault();
        radio.checked = true;
        rows.forEach((r) => { r.style.borderColor = '#e5e7eb'; });
        label.style.borderColor = '#0f766e';
      };
      if (tpl.id === preselectId) label.style.borderColor = '#0f766e';
      rows.push(label);
      list.appendChild(label);
    }

    const currentId = (): string => {
      const checked = list.querySelector<HTMLInputElement>('input[type=radio]:checked');
      return checked ? checked.value : '';
    };

    const btnRow = document.createElement('div');
    btnRow.style.cssText = 'display:flex;gap:10px';
    const confirmBtn = makeBtn('使用该模板分享', true, () => {
      selectedId = currentId();
      if (selectedId) writeLastPick(docType, selectedId);
      close();
    });
    const cancelBtn = makeBtn('取消', false, () => { close(); });
    btnRow.appendChild(confirmBtn);
    btnRow.appendChild(cancelBtn);

    card.appendChild(title);
    card.appendChild(sub);
    card.appendChild(list);
    card.appendChild(btnRow);
    wrap.appendChild(card);
    wrap.onclick = (e) => { if (e.target === wrap) close(); };
    document.body.appendChild(wrap);
  });
}
